import type { ReactNode } from 'react';
import Divider from '@mui/material/Divider';
import {
    EXCAVATION_METHOD_OPTIONS,
    FORM_OPTIONS,
    ORIENTATION_OPTIONS
} from '@ast-data/shared';
import {
    bindFormFields,
    type FormFieldBinding
} from '../../../components/formFields';
import FormRow from '../../../components/FormRow';
import FormSectionBox from '../../../components/FormSectionBox';
import CourseListEditor from '../CourseListEditor';
import type { CourseRow } from '../courseRows';
import StratumListEditor from '../StratumListEditor';
import type { StratumRow } from '../stratumRows';
import type { DescriptionFormValues } from './descriptionFormValues';

/**
 * How big the description is and what shape it takes.
 *
 * Form and Ausrichtung come FIRST: which shape it is decides what measures it —
 * a circle by its Durchmesser, everything else by Länge × Breite — so the
 * question is asked before the boxes it governs.
 *
 * The MEASURES themselves are passed in: a Planum is measured by length, width,
 * diameter, depth and its Abstich, a Profil only by width and depth.
 *
 * A „baulicher Bestand" is measured DIFFERENTLY — in Lagen, each with its own
 * figures — so passing `courses` swaps the flat measures and the Schichten for
 * the Lagen list, and adds the Grabungsmethode beside Form und Ausrichtung.
 *
 * Höhe oben / Höhe unten (NHN) are HIDDEN for now on the flat form — the fields
 * stay in the form values, so stored heights survive a save. A Lage states both
 * outright, where they are the point.
 */
export default function DimensionsSection({
    binding,
    titlePrefix,
    labelPrefix,
    measures,
    formOptions = FORM_OPTIONS,
    orientationOptions = ORIENTATION_OPTIONS,
    strata,
    onStrataChange,
    strataLength,
    strataDepth,
    courses,
    onCoursesChange
}: {
    binding: FormFieldBinding<DescriptionFormValues>;
    titlePrefix: string;
    /** The field-label catalog of the owning form (`planum.field`/`profile.field`). */
    labelPrefix: string;
    /** The measure rows the owning form is measured by. */
    measures: ReactNode;
    /** What the Form picker offers — the Planum's outlines by default. */
    formOptions?: readonly string[];
    /**
     * What the Ausrichtung picker offers. The 16 compass pairs by default; the
     * Planum widens it by „Keine" while its Form is „kreisförmig",
     * which a Profil never is.
     */
    orientationOptions?: readonly string[];
    /**
     * The Schichten and their own measures. Beneath the description's overall
     * ones, because that is what they subdivide — and seeded from the Schicht
     * marks on its sketches (see sketchStrata).
     */
    strata: StratumRow[];
    onStrataChange: (rows: StratumRow[]) => void;
    /** Whether a Schicht row shows Länge (Planum) and Tiefe (Profil). */
    strataLength: boolean;
    strataDepth: boolean;
    /**
     * The Lagen of a „baulicher Bestand". Present ONLY for that kind of Befund,
     * and then in place of everything above (see the note on the section).
     */
    courses?: CourseRow[];
    onCoursesChange?: (rows: CourseRow[]) => void;
}) {
    const { select, suggest } = bindFormFields(binding);
    // The two answers, as the closed picker takes them. Stored as the German
    // word itself, like every other vocabulary on this form.
    const methodOptions = EXCAVATION_METHOD_OPTIONS.map((method) => ({
        value: method,
        label: method
    }));
    const isStructure = !!courses && !!onCoursesChange;
    return (
        <FormSectionBox titlePrefix={titlePrefix} id="dimensions">
            <FormRow>
                {suggest('form', formOptions)}
                {suggest('orientation', orientationOptions)}
            </FormRow>
            {/* Its OWN row, and not a third column beside Form und Ausrichtung:
                FormRow counts its columns with `Children.count`, which counts
                the `false` of a hidden child too — a conditional field in that
                row would leave every ordinary description a two-field row split
                three ways.

                A closed list: künstliche or natürliche Schichten is the whole
                answer, so this is a select — clearable, because it may simply
                not have been noted. */}
            {isStructure && (
                <FormRow>
                    {select('excavationMethod', methodOptions, {
                        clearable: true
                    })}
                </FormRow>
            )}
            {isStructure ? (
                <>
                    {/* A rule above the list, as every repeater in these forms
                        has: it separates the fields that describe the
                        description as a whole from the rows that subdivide
                        it. */}
                    <Divider />
                    <CourseListEditor
                        rows={courses}
                        onChange={onCoursesChange}
                        labelPrefix={labelPrefix}
                    />
                </>
            ) : (
                <>
                    {measures}
                    <Divider />
                    <StratumListEditor
                        rows={strata}
                        onChange={onStrataChange}
                        showLength={strataLength}
                        showDepth={strataDepth}
                        // Length is the Planum's measure; so is a diameter.
                        showDiameter={strataLength}
                    />
                </>
            )}
        </FormSectionBox>
    );
}
